import { watch, type App } from 'vue';

import type { Action, ActionProps } from '@cvent/analytics/Action';
import type { Store } from 'vuex';

import {
  canPublish,
  init,
  track,
  updateBaseProperties,
  type CventActivitiesBaseProperties,
  type CventActivitiesInitOptions
} from '@/analytics/cvent/core';
import { useStore, type RootState } from '@/store';

/** Init options resolved by the caller; base properties come from the store. */
type CventAnalyticsOptions = Omit<CventActivitiesInitOptions, 'baseProperties'>;

/** Plugin install options: the init options plus the store to source identity from. */
type CventAnalyticsPluginOptions = CventAnalyticsOptions & { store: Store<RootState> };

/**
 * Vue adapter over the framework-agnostic Cvent Activities core singleton.
 *
 * `collectBaseProperties()` exposes the four Cvent identity keys plus the Cvent
 * `environment` — `attendeeId` and `eventId` come from the event/user state;
 * `accountMappingId` and `sessionId` are sourced from the survey endpoint;
 * `environment` is the event's `cvent_environment`. All flow through
 * `event/getCventIdentity`. The core's `canPublish()` still gates `init()`/`track()`
 * until every identity key resolves for a given attendee — `environment` is
 * published alongside but does not gate.
 */
export function useCventAnalytics() {
  const store = useStore();

  function collectBaseProperties(): Partial<CventActivitiesBaseProperties> {
    const identity = store.getters['event/getCventIdentity'];
    return {
      attendeeId: identity.attendeeId,
      eventId: identity.eventId,
      accountMappingId: identity.accountMappingId,
      sessionId: identity.sessionId,
      environment: identity.environment
    };
  }

  function initialize(options: CventAnalyticsOptions): void {
    const baseProperties = collectBaseProperties();
    // Gate on the core's identity check (as the plugin does) so init() is only
    // ever called once every identity key is present — the cast below is then
    // guarded rather than an unconditional widening of the Partial.
    if (!canPublish(baseProperties as CventActivitiesBaseProperties)) {
      return;
    }
    init({ ...options, baseProperties: baseProperties as CventActivitiesBaseProperties });
  }

  function trackAction<A extends ActionProps>(action: Action<A>): void {
    track(action);
  }

  return { collectBaseProperties, initialize, track: trackAction };
}

/**
 * Vue plugin that keeps the Cvent Activities singleton in step with the store's
 * Cvent identity.
 *
 * The store is passed in explicitly via the install options
 * (`app.use(CventAnalyticsPlugin, { store, ... })`) rather than read off
 * `app.config.globalProperties.$store`, so the dependency is explicit and does
 * not rely on install ordering. The watcher fires immediately (initializing
 * straight away when identity is already complete) and again whenever identity
 * resolves later, e.g. after login. `init()` is idempotent, so repeated fires
 * are safe.
 *
 * Publishing is additionally gated on the `USE_CVENT_ATTENDEE_ACTIVITY` Harness
 * flag, mirrored into `event.useCventAttendeeActivity` by the feature-flag service
 * so the watcher reads it reactively from the store — flipping the flag re-fires
 * the watcher on its own, no identity change required.
 */
export const CventAnalyticsPlugin = {
  install(_app: App, options: CventAnalyticsPluginOptions): void {
    const { store, ...initOptions } = options;

    watch(
      () => ({
        identity: store.getters['event/getCventIdentity'],
        enabled: store.state.event.useCventAttendeeActivity
      }),
      ({ identity, enabled }) => {
        if (!enabled) {
          return;
        }
        const baseProperties: Partial<CventActivitiesBaseProperties> = {
          attendeeId: identity.attendeeId,
          eventId: identity.eventId,
          accountMappingId: identity.accountMappingId,
          sessionId: identity.sessionId,
          environment: identity.environment
        };
        if (canPublish(baseProperties as CventActivitiesBaseProperties)) {
          init({ ...initOptions, baseProperties: baseProperties as CventActivitiesBaseProperties });
          updateBaseProperties(baseProperties);
        }
      },
      { immediate: true }
    );
  }
};
